/**
 * WebSocket Context - Phase 5: Real-time Collaboration
 * Provides WebSocket connection and methods throughout the app
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useWebSocket, UseWebSocketReturn } from '@/hooks/useWebSocket';
import { Socket } from 'socket.io-client';

// Context type
interface WebSocketContextType extends UseWebSocketReturn {
  // Additional helper methods can be added here
}

// Create context with undefined default
const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Provider component
interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const webSocket = useWebSocket();

  return (
    <WebSocketContext.Provider value={webSocket}>
      {children}
    </WebSocketContext.Provider>
  );
}

// Custom hook to use the WebSocket context
export function useWebSocketContext(): WebSocketContextType {
  const context = useContext(WebSocketContext);

  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }

  return context;
}

// Helper hook to listen to specific events
export function useWebSocketEvent(
  eventName: string,
  handler: (data: any) => void,
  dependencies: any[] = []
) {
  const { socket } = useWebSocketContext();

  React.useEffect(() => {
    if (!socket) return;

    socket.on(eventName, handler);

    return () => {
      socket.off(eventName, handler);
    };
  }, [socket, eventName, ...dependencies]);
}

// Helper hook to join/leave rooms automatically
export function useWebSocketRoom(roomId: string | null) {
  const { joinRoom, leaveRoom, isConnected } = useWebSocketContext();

  React.useEffect(() => {
    if (!roomId || !isConnected) return;

    joinRoom(roomId);

    return () => {
      leaveRoom(roomId);
    };
  }, [roomId, isConnected, joinRoom, leaveRoom]);
}

// Helper hook for brand-specific rooms
export function useBrandRoom(brandId: string | null) {
  const { socket, isConnected } = useWebSocketContext();

  React.useEffect(() => {
    if (!brandId || !isConnected || !socket) return;

    const roomId = `brand:${brandId}`;
    socket.emit('join:brand', brandId);

    return () => {
      socket.emit('leave:room', roomId);
    };
  }, [brandId, isConnected, socket]);
}

// Helper hook for campaign-specific rooms
export function useCampaignRoom(campaignId: string | null) {
  const { socket, isConnected } = useWebSocketContext();

  React.useEffect(() => {
    if (!campaignId || !isConnected || !socket) return;

    const roomId = `campaign:${campaignId}`;
    socket.emit('join:campaign', campaignId);

    return () => {
      socket.emit('leave:room', roomId);
    };
  }, [campaignId, isConnected, socket]);
}

// Helper hook for execution-specific rooms
export function useExecutionRoom(executionId: string | null) {
  const { socket, isConnected } = useWebSocketContext();

  React.useEffect(() => {
    if (!executionId || !isConnected || !socket) return;

    const roomId = `execution:${executionId}`;
    socket.emit('join:execution', executionId);

    return () => {
      socket.emit('leave:room', roomId);
    };
  }, [executionId, isConnected, socket]);
}

// Helper hook for agent-specific rooms
export function useAgentRoom(agentId: string | null) {
  const { socket, isConnected } = useWebSocketContext();

  React.useEffect(() => {
    if (!agentId || !isConnected || !socket) return;

    const roomId = `agent:${agentId}`;
    socket.emit('join:agent', agentId);

    return () => {
      socket.emit('leave:room', roomId);
    };
  }, [agentId, isConnected, socket]);
}

// Export context for advanced usage
export { WebSocketContext };
